import { Connect } from '@genesislcap/foundation-comms';
import { MaturityOrder } from './deal-series.types';
import {
  isSplitChildOrder,
  isSplitMaturityGridOrder,
  normalizeBondOrderReply,
} from './split-order.util';
import { normalizeNumericValueForStorage } from './deal-series.util';

function coerceEpochMs(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function coerceNumericField(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const normalized = normalizeNumericValueForStorage(
    typeof value === 'string' || typeof value === 'number' ? value : undefined,
  );
  return normalized ?? undefined;
}

function coerceBooleanField(value: unknown): boolean | undefined {
  if (value == null || value === '') return undefined;
  if (value === true || value === 'true' || value === 'Y') return true;
  if (value === false || value === 'false' || value === 'N') return false;
  return undefined;
}

/** Normalizes an ALL_MATURITY_ORDERS dataserver row for submit-modal supplemental sole maturities. */
export function normalizeAllMaturityGridOrder(record: Record<string, unknown>): MaturityOrder {
  const order = normalizeBondOrderReply(record);
  const recordFlags = record as {
    IS_SPLIT_MATURITY?: boolean;
    isSplitMaturity?: boolean;
  };
  const isSplitMaturity =
    order.IS_SPLIT_MATURITY === true ||
    order.HAS_SPLIT === true ||
    recordFlags.IS_SPLIT_MATURITY === true ||
    recordFlags.isSplitMaturity === true;
  return {
    ...order,
    HAS_SPLIT: isSplitMaturity,
    IS_SPLIT_MATURITY: isSplitMaturity,
  };
}

/** Maps camelCase req-rep fields to the UPPER_SNAKE keys the grid expects. */
function mapSubmitOrderReplyFields(record: Record<string, unknown>): Record<string, unknown> {
  const mapped: Record<string, unknown> = { ...record };
  const pairs: [string, string][] = [
    ['orderId', 'ORDER_ID'],
    ['userName', 'USER_NAME'],
    ['maturityId', 'MATURITY_ID'],
    ['seriesId', 'SERIES_ID'],
    ['dealId', 'DEAL_ID'],
    ['counterpartyId', 'COUNTERPARTY_ID'],
    ['orderStatus', 'ORDER_STATUS'],
    ['orderType', 'ORDER_TYPE'],
    ['retailType', 'RETAIL_TYPE'],
    ['priorityType', 'PRIORITY_TYPE'],
    ['quantity', 'QUANTITY'],
    ['submittedQuantity', 'SUBMITTED_QUANTITY'],
    ['minimumOrderAmount', 'MINIMUM_ORDER_AMOUNT'],
    ['maximumOrderAmount', 'MAXIMUM_ORDER_AMOUNT'],
    ['orderAmountIncrement', 'ORDER_AMOUNT_INCREMENT'],
    ['isAutoResetOrderAmount', 'IS_AUTO_RESET_ORDER_AMOUNT'],
    ['isZeroCancelIntent', 'IS_ZERO_CANCEL_INTENT'],
    ['accountCode', 'ACCOUNT_CODE'],
    ['zipCode', 'ZIP_CODE'],
    ['comment', 'COMMENT'],
    ['muniStrip', 'MUNI_STRIP'],
    ['muniAon', 'MUNI_AON'],
    ['flexMaturity', 'FLEX_MATURITY'],
    ['flexCoupon', 'FLEX_COUPON'],
    ['isConfirmed', 'IS_CONFIRMED'],
    ['errorMessage', 'ERROR_MESSAGE'],
    ['lineNo', 'LINE_NO'],
    ['hasSplit', 'HAS_SPLIT'],
    ['splitConditionsVarious', 'SPLIT_CONDITIONS_VARIOUS'],
    ['reconfirmEligible', 'RECONFIRM_ELIGIBLE'],
    ['maturityDate', 'MATURITY_DATE'],
    ['putDate', 'PUT_DATE'],
    ['maturityAmount', 'MATURITY_AMOUNT'],
    ['maturityDescription', 'MATURITY_DESCRIPTION'],
    ['maturityStatus', 'MATURITY_STATUS'],
    ['cusip', 'CUSIP'],
    ['coupon', 'COUPON'],
    ['price', 'PRICE'],
    ['yield', 'YIELD'],
    ['spread', 'SPREAD'],
    ['taxTreasurySpread', 'TAX_TREASURY_SPREAD'],
    ['taxTreasuryCoupon', 'TAX_TREASURY_COUPON'],
    ['taxTreasuryMaturityDate', 'TAX_TREASURY_MATURITY_DATE'],
    ['multiples', 'MULTIPLES'],
    ['couponChanged', 'COUPON_CHANGED'],
    ['yieldChanged', 'YIELD_CHANGED'],
    ['priceChanged', 'PRICE_CHANGED'],
    ['maturityAmountChanged', 'MATURITY_AMOUNT_CHANGED'],
    ['maturityOverrideFitch', 'MATURITY_OVERRIDE_FITCH'],
    ['maturityOverrideMoodys', 'MATURITY_OVERRIDE_MOODYS'],
    ['maturityOverrideSandp', 'MATURITY_OVERRIDE_SANDP'],
    ['maturityOverrideKroll', 'MATURITY_OVERRIDE_KROLL'],
    ['maturityOverrideInsurance', 'MATURITY_OVERRIDE_INSURANCE'],
    ['callDateOverride', 'CALL_DATE_OVERRIDE'],
    ['mandatoryTender', 'MANDATORY_TENDER'],
    ['securityType', 'SECURITY_TYPE'],
    ['sinkingFundSchedule', 'SINKING_FUND_SCHEDULE'],
  ];
  for (const [camel, upper] of pairs) {
    if (mapped[camel] !== undefined) {
      mapped[upper] = mapped[camel];
    }
  }
  for (const key of ['MATURITY_ID', 'SERIES_ID', 'DEAL_ID'] as const) {
    if (mapped[key] != null && mapped[key] !== '') {
      mapped[key] = String(mapped[key]);
    }
  }
  return mapped;
}

/** Normalizes a DEAL_SUBMIT_ORDERS req-rep row into a MaturityOrder for submit/cancel flows. */
export function normalizeSubmitOrderReply(record: Record<string, unknown>): MaturityOrder {
  const mapped = mapSubmitOrderReplyFields(record);
  const order = normalizeBondOrderReply(mapped);
  return {
    ...order,
    MATURITY_DATE: coerceEpochMs(mapped.MATURITY_DATE) ?? order.MATURITY_DATE,
    PUT_DATE: coerceEpochMs(mapped.PUT_DATE) ?? order.PUT_DATE,
    TAX_TREASURY_MATURITY_DATE:
      coerceEpochMs(mapped.TAX_TREASURY_MATURITY_DATE) ?? order.TAX_TREASURY_MATURITY_DATE,
    MATURITY_AMOUNT: coerceNumericField(mapped.MATURITY_AMOUNT) ?? order.MATURITY_AMOUNT,
    MATURITY_PRESENT_VALUE:
      coerceNumericField(mapped.MATURITY_PRESENT_VALUE) ?? order.MATURITY_PRESENT_VALUE,
    MINIMUM_ORDER_AMOUNT:
      coerceNumericField(mapped.MINIMUM_ORDER_AMOUNT) ?? order.MINIMUM_ORDER_AMOUNT,
    MAXIMUM_ORDER_AMOUNT:
      coerceNumericField(mapped.MAXIMUM_ORDER_AMOUNT) ?? order.MAXIMUM_ORDER_AMOUNT,
    MUNI_STRIP: coerceBooleanField(mapped.MUNI_STRIP) ?? order.MUNI_STRIP,
    MUNI_AON: coerceBooleanField(mapped.MUNI_AON) ?? order.MUNI_AON,
    IS_AUTO_RESET_ORDER_AMOUNT:
      coerceBooleanField(mapped.IS_AUTO_RESET_ORDER_AMOUNT) ?? order.IS_AUTO_RESET_ORDER_AMOUNT,
    IS_ZERO_CANCEL_INTENT:
      coerceBooleanField(mapped.IS_ZERO_CANCEL_INTENT) ?? order.IS_ZERO_CANCEL_INTENT,
    IS_CONFIRMED: coerceBooleanField(mapped.IS_CONFIRMED) ?? order.IS_CONFIRMED,
    SPLIT_CONDITIONS_VARIOUS:
      coerceBooleanField(mapped.SPLIT_CONDITIONS_VARIOUS) ?? order.SPLIT_CONDITIONS_VARIOUS,
    RECONFIRM_ELIGIBLE:
      coerceBooleanField(mapped.RECONFIRM_ELIGIBLE) ?? order.RECONFIRM_ELIGIBLE,
    IS_SPLIT_MATURITY: false,
    HAS_SPLIT: false,
  };
}

/** Stable JSON signature to skip redundant grid refreshes when poll data is unchanged. */
export function getSubmitOrdersSnapshotSignature(orders: MaturityOrder[]): string {
  return JSON.stringify(orders);
}

export function areSubmitOrderMapsEqual(
  left: Map<string, MaturityOrder[]> | undefined,
  right: Map<string, MaturityOrder[]>,
): boolean {
  if (!left || left.size !== right.size) return false;
  for (const [seriesId, rows] of right) {
    const existing = left.get(seriesId);
    if (!existing || existing.length !== rows.length) return false;
    for (let i = 0; i < rows.length; i++) {
      if (getSubmitOrdersSnapshotSignature([existing[i]]) !== getSubmitOrdersSnapshotSignature([rows[i]])) {
        return false;
      }
    }
  }
  return true;
}

/** @deprecated use normalizeSubmitOrderReply */
export const normalizeDataserverSubmitOrder = normalizeSubmitOrderReply;

function extractSubmitOrderReplyRecords(raw: unknown): Record<string, unknown>[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.filter(
      (item): item is Record<string, unknown> => item != null && typeof item === 'object',
    );
  }
  if (typeof raw !== 'object') return [];

  const response = raw as Record<string, unknown>;
  if (response.MESSAGE_TYPE === 'ERROR' || response.MESSAGE_TYPE === 'MSG_NACK') {
    return [];
  }

  const reply = response.REPLY ?? response.reply ?? response.DETAILS;
  if (Array.isArray(reply)) {
    return reply.filter(
      (item): item is Record<string, unknown> => item != null && typeof item === 'object',
    );
  }
  if (reply != null && typeof reply === 'object') {
    const obj = reply as Record<string, unknown>;
    const values = Object.values(obj);
    if (
      values.length > 0 &&
      values.every((value) => value != null && typeof value === 'object')
    ) {
      return values as Record<string, unknown>[];
    }
    return [obj];
  }
  return [];
}

export async function fetchDealSubmitOrders(
  connect: Connect,
  dealId: string | number,
): Promise<MaturityOrder[]> {
  const parsedDealId = Number(dealId);
  if (Number.isNaN(parsedDealId)) {
    console.error('fetchDealSubmitOrders: invalid dealId', dealId);
    return [];
  }
  const response = await connect.request('REQ_DEAL_SUBMIT_ORDERS', {
    REQUEST: {
      DEAL_ID: parsedDealId,
      dealId: parsedDealId,
    },
  });
  if (response?.MESSAGE_TYPE === 'MSG_NACK' || response?.MESSAGE_TYPE === 'ERROR') {
    console.error('fetchDealSubmitOrders: request failed', response);
    return [];
  }
  return extractSubmitOrderReplyRecords(response).map(normalizeSubmitOrderReply);
}

/** Split child rows from DEAL_SUBMIT_ORDERS (LINE_NO > 0). */
export function getSplitChildOrders(orders: MaturityOrder[]): MaturityOrder[] {
  return orders.filter(isSplitChildOrder);
}

export function buildSplitChildrenByMaturityId(orders: MaturityOrder[]): Map<string, MaturityOrder[]> {
  const map = new Map<string, MaturityOrder[]>();
  for (const child of getSplitChildOrders(orders)) {
    const maturityId = String(child.MATURITY_ID ?? '');
    if (!maturityId) continue;
    const list = map.get(maturityId) ?? [];
    list.push(child);
    map.set(maturityId, list);
  }
  for (const [maturityId, list] of map) {
    map.set(
      maturityId,
      [...list].sort((a, b) => (a.LINE_NO ?? a.SPLIT_LINE_NO ?? 0) - (b.LINE_NO ?? b.SPLIT_LINE_NO ?? 0)),
    );
  }
  return map;
}

/** Map lookup tolerant of string/number series id keys. */
export function getSeriesOrdersFromMap(
  map: Map<string, MaturityOrder[]> | undefined,
  seriesId: string | number | undefined,
): MaturityOrder[] {
  if (!map || seriesId == null || seriesId === '') return [];
  const key = String(seriesId);
  if (map.has(key)) return map.get(key) ?? [];
  for (const [mapKey, orders] of map) {
    if (String(mapKey) === key) return orders;
  }
  return [];
}

/**
 * Orders snapshot for submit-modal filtering/supplemental logic.
 * Persisted rows (incl. conditions summary fields) come from DEAL_SUBMIT_ORDERS;
 * sole maturities with no persisted order are supplemented from the main grid.
 */
const isFieldMissing = (value: unknown): boolean =>
  value == null || String(value).trim() === '';

/**
 * DEAL_SUBMIT_ORDERS does not carry the zero-coupon principal (MATURITY_PRESENT_VALUE),
 * so backfill it (and SECURITY_TYPE) from the main-grid maturity snapshot, which is the
 * authoritative source for bond-level fields. Only fills when absent so server values win.
 */
function enrichSubmitOrderBondFields(
  submitOrder: MaturityOrder,
  mainGridByMaturityId: Map<string, MaturityOrder>,
): MaturityOrder {
  const maturityId = String(submitOrder.MATURITY_ID ?? '');
  if (maturityId === '') return submitOrder;
  const mainGridOrder = mainGridByMaturityId.get(maturityId);
  if (!mainGridOrder) return submitOrder;
  const patch: Partial<MaturityOrder> = {};
  if (isFieldMissing(submitOrder.MATURITY_PRESENT_VALUE) && !isFieldMissing(mainGridOrder.MATURITY_PRESENT_VALUE)) {
    patch.MATURITY_PRESENT_VALUE = mainGridOrder.MATURITY_PRESENT_VALUE;
  }
  if (isFieldMissing(submitOrder.SECURITY_TYPE) && !isFieldMissing(mainGridOrder.SECURITY_TYPE)) {
    patch.SECURITY_TYPE = mainGridOrder.SECURITY_TYPE;
  }
  return Object.keys(patch).length === 0 ? submitOrder : { ...submitOrder, ...patch };
}

export function getSubmitModalMaturityOrders(
  ordersBySeriesId: Map<string, MaturityOrder[]> | undefined,
  submitOrdersBySeriesId: Map<string, MaturityOrder[]> | undefined,
  seriesId: string | number | undefined,
): MaturityOrder[] {
  const fromSubmit = getSeriesOrdersFromMap(submitOrdersBySeriesId, seriesId);
  const mainGridSeriesOrders = getSeriesOrdersFromMap(ordersBySeriesId, seriesId);
  const fromMainGrid = mainGridSeriesOrders.filter((order) => !isSplitMaturityGridOrder(order));

  // No persisted orders yet: fall back to the main-grid maturity snapshot for this series.
  if (fromSubmit.length === 0) return fromMainGrid;

  // Include split anchors here: a split maturity's only main-grid row is the rollup anchor, and it
  // is the authoritative carrier of the bond-level principal/security type for its split children.
  const mainGridByMaturityId = new Map<string, MaturityOrder>();
  for (const order of mainGridSeriesOrders) {
    const maturityId = String(order.MATURITY_ID ?? '');
    if (maturityId !== '' && !mainGridByMaturityId.has(maturityId)) {
      mainGridByMaturityId.set(maturityId, order);
    }
  }
  const enrichedSubmit = fromSubmit.map((order) =>
    enrichSubmitOrderBondFields(order, mainGridByMaturityId),
  );

  const submitMaturityIds = new Set(
    enrichedSubmit.map((order) => String(order.MATURITY_ID ?? '')).filter((id) => id !== ''),
  );
  const supplementalSoleOrders = fromMainGrid.filter((order) => {
    const maturityId = String(order.MATURITY_ID ?? '');
    return maturityId !== '' && isSoleMaturityGridRow(order) && !submitMaturityIds.has(maturityId);
  });

  return [...enrichedSubmit, ...supplementalSoleOrders];
}

/** Stable row key for submit-modal grid dedupe (first split child may reuse anchor ORDER_ID). */
export function getSubmitModalRowKey(order: MaturityOrder): string | null {
  const orderId = order.ORDER_ID;
  if (!orderId) return null;
  const lineNo = order.LINE_NO ?? order.SPLIT_LINE_NO ?? 0;
  return lineNo > 0 ? `${orderId}-${lineNo}` : orderId;
}

/** Replace split rollup anchors with persisted split-child rows from DEAL_SUBMIT_ORDERS. */
export function getSubmitModalGridAdjustments(
  gridOrders: MaturityOrder[],
  submitSeriesOrders: MaturityOrder[],
): { remove: MaturityOrder[]; add: MaturityOrder[] } {
  const splitChildrenByMaturity = buildSplitChildrenByMaturityId(submitSeriesOrders);

  const remove = gridOrders.filter((row) => {
    if (isSplitChildOrder(row)) return false;
    if (isSplitMaturityGridOrder(row)) return true;
    const maturityId = String(row.MATURITY_ID ?? '');
    return maturityId !== '' && splitChildrenByMaturity.has(maturityId);
  });

  const removedRows = new Set(remove);
  const gridRowKeys = new Set(
    gridOrders
      .filter((row) => !removedRows.has(row))
      .map(getSubmitModalRowKey)
      .filter((key): key is string => !!key),
  );

  const add = getSplitChildOrders(submitSeriesOrders).filter((child) => {
    const key = getSubmitModalRowKey(child);
    return !!key && !gridRowKeys.has(key);
  });

  return { remove, add };
}

/** True when the main-grid row is a sole (non-split) maturity, including empty rows with no order yet. */
export function isSoleMaturityGridRow(order: MaturityOrder): boolean {
  if (isSplitMaturityGridOrder(order)) return false;
  if ((order.LINE_NO != null && order.LINE_NO > 0) || order.IS_SPLIT === true) return false;
  return order.MATURITY_ID != null && order.MATURITY_ID !== '';
}

/**
 * Sole maturity rows from the main grid (ALL_MATURITY_ORDERS) missing in DEAL_SUBMIT_ORDERS.
 * Includes empty maturities with no amount, status, conditions, or persisted ORDER_ID.
 */
export function getSupplementalSoleOrdersForSubmitModal(
  gridOrders: MaturityOrder[],
  maturityOrders: MaturityOrder[],
): MaturityOrder[] {
  const gridMaturityIds = new Set<string>();
  for (const row of gridOrders) {
    if (row.MATURITY_ID != null) gridMaturityIds.add(String(row.MATURITY_ID));
  }

  const supplemental: MaturityOrder[] = [];
  for (const order of maturityOrders) {
    if (!isSoleMaturityGridRow(order)) continue;
    const maturityId = String(order.MATURITY_ID);
    if (gridMaturityIds.has(maturityId)) continue;
    supplemental.push({
      ...order,
      IS_SPLIT_MATURITY: false,
      HAS_SPLIT: false,
    });
  }
  return supplemental;
}

export function groupSubmitPreviewOrdersBySeriesId(orders: MaturityOrder[]): Map<string, MaturityOrder[]> {
  const map = new Map<string, MaturityOrder[]>();
  for (const order of orders) {
    const seriesId = String(order.SERIES_ID ?? '');
    if (!seriesId) continue;
    const list = map.get(seriesId) ?? [];
    list.push(order);
    map.set(seriesId, list);
  }
  for (const [seriesId, list] of map) {
    map.set(
      seriesId,
      [...list].sort((a, b) => {
        const dateA = Number(a.MATURITY_DATE) || 0;
        const dateB = Number(b.MATURITY_DATE) || 0;
        if (dateA !== dateB) return dateA - dateB;
        return (a.LINE_NO ?? a.SPLIT_LINE_NO ?? 0) - (b.LINE_NO ?? b.SPLIT_LINE_NO ?? 0);
      }),
    );
  }
  return map;
}
